// src/app/shared/store/catalog/catalog.adapters.ts

import { AppLanguage, getActiveAppLanguage } from '../../i18n/app-language.util';
import { buildApiUrl } from '../../api/url.util';
import { environment } from 'src/environments/environment';
import {
  CatalogCardVm,
  CatalogFolderDto,
  CatalogFolderNodeDto,
  CatalogFolderPathNodeDto,
  CatalogFolderVm,
  CatalogItemGroupDto,
  Uuid,
} from "./catalog.api.types";

function normalizeLocalizedFolderName(value: string | null | undefined): string {
  return (value ?? '').trim();
}

function normalizeCatalogImageSrc(value: string | null | undefined): string | null {
  const normalized = (value ?? '').trim();
  return normalized.length > 0 ? normalized : null;
}

function resolveCatalogImageSrc(value: string | null | undefined, baseUrl: string): string | null {
  const normalized = normalizeCatalogImageSrc(value);
  if (!normalized) return null;

  if (/^(https?:)?\/\//i.test(normalized) || /^(data|blob):/i.test(normalized)) {
    return normalized;
  }

  if (!baseUrl.trim()) return normalized;

  if (normalized.startsWith('/') || normalized.startsWith('media/catalog/')) {
    return buildApiUrl(baseUrl, normalized);
  }

  return normalized;
}

export function getCatalogFolderDisplayName(
  folder: Pick<CatalogFolderDto, 'name' | 'name_zh' | 'name_en'> | Pick<CatalogFolderPathNodeDto, 'name' | 'name_zh' | 'name_en'>,
  language: AppLanguage = getActiveAppLanguage(),
): string {
  const ruName = normalizeLocalizedFolderName(folder.name);
  const zhName = normalizeLocalizedFolderName(folder.name_zh);
  const enName = normalizeLocalizedFolderName(folder.name_en);

  if (language === 'zh' && zhName) return zhName;
  if (language === 'en' && enName) return enName;

  return ruName || zhName || enName || '';
}

export function getCatalogFolderPathDisplayName(
  path: ReadonlyArray<Pick<CatalogFolderPathNodeDto, 'name' | 'name_zh' | 'name_en'>>,
  language: AppLanguage = getActiveAppLanguage(),
): string {
  return path
    .map((node) => getCatalogFolderDisplayName(node, language))
    .filter((name) => name.trim().length > 0)
    .join(' / ');
}

export function normalizeFoldersPayload(payload: any): CatalogFolderDto[] {
  // поддержка обоих форматов:
  // 1) { items: CatalogFolderDto[] }
  // 2) { tree: CatalogFolderNodeDto[], flat: {...} }
  if (payload?.items && Array.isArray(payload.items)) return payload.items;

  // если tree — расплющим в список
  if (payload?.tree && Array.isArray(payload.tree)) {
    const out: CatalogFolderDto[] = [];
    const walk = (n: CatalogFolderNodeDto, depth = 0, path: CatalogFolderPathNodeDto[] = []) => {
      const nextPath = [
        ...path,
        {
          id: n.id,
          parent_id: n.parent_id ?? null,
          name: n.name,
          name_zh: n.name_zh ?? null,
          name_en: n.name_en ?? null,
        },
      ];

      out.push({
        id: n.id,
        name: n.name,
        name_zh: n.name_zh ?? null,
        name_en: n.name_en ?? null,
        parent_id: n.parent_id ?? null,
        sort: n.sort ?? 0,
        depth,
        has_children: (n.children?.length ?? 0) > 0,
        path: nextPath,
        created_at: n.created_at,
        updated_at: n.updated_at,
      });
      for (const c of n.children ?? []) walk(c, depth + 1, nextPath);
    };
    for (const root of payload.tree) walk(root);
    return out;
  }

  // flat map => values()
  if (payload?.flat && typeof payload.flat === 'object') {
    return Object.values(payload.flat) as any;
  }

  return [];
}

export function folderDtoToVm(f: CatalogFolderDto): CatalogFolderVm {
  return {
    id: f.id,
    name: getCatalogFolderDisplayName(f),
    parentId: f.parent_id ?? null,
    sort: f.sort ?? 0,
    depth: f.depth,
    hasChildren: f.has_children,
    path: f.path,
    routerLink: ['/app/catalog', f.id],
  };
}

export function normalizeCatalogImages(
  image?: string | null,
  images?: ReadonlyArray<string | null | undefined>,
  imageBaseUrl: string = environment.catalogImageBaseUrl,
): string[] {
  const gallery = (images ?? [])
    .map((src) => resolveCatalogImageSrc(src, imageBaseUrl))
    .filter((src): src is string => src !== null);

  if (gallery.length > 0) return gallery;

  const primary = resolveCatalogImageSrc(image, imageBaseUrl);
  return primary ? [primary] : [];
}

/**
 * Карточка модели (группы) => Card VM.
 * serial: если в группе есть серийники — показываем первый (или null).
 * quantity: total_qty группы.
 */
export function groupToCardVm(g: CatalogItemGroupDto): CatalogCardVm {
  const firstSerial = g.items?.find((x) => !!x.serial)?.serial ?? null;
  const images = normalizeCatalogImages(g.image, g.images);

  return {
    id: g.model_id,
    name: g.name,
    description: g.description ?? null,
    serial: firstSerial,
    totalQuantity: Number(g.total_qty ?? 0),
    image: images.length > 0 ? images : undefined,
  };
}

/**
 * Для экранов “внутри папки” — фильтруем по folder_id:
 * - root: folder_id === null
 * - exact: folder_id === selectedId
 */
export function isInFolder(g: CatalogItemGroupDto, folderId: Uuid | null): boolean {
  return (g.folder_id ?? null) === folderId;
}
